<template>
  <div
    class="bg-white p-6 lg:p-8 w-full relative min-h-[650px] rounded-xl"
    :class="isManagerContext ? 'rounded-tl-none' : ''"
  >
    <button
      v-if="isAdmin && isManagerContext"
      class="btn btn-trans-black m-0 px-4 py-3"
      :class="
        activeView === VIEW.CONNECTIONS
          ? 'btn-trans-black absolute right-8 top-6'
          : 'btn-black'
      "
      @click="routeToView"
    >
      <span class="sm:hidden" v-html="viewButtonIcon" />
      <span class="hidden sm:block">
        {{ viewButtonText }}
      </span>
    </button>

    <FormUpsertConnection v-if="activeView === VIEW.UPSERT" />

    <div v-else-if="isAdmin" class="bg-white w-full">
      <div
        class="flex bg-truegray-100 w-[max-content] rounded-lg overflow-hidden mb-6"
      >
        <button
          v-for="(tab, i) in CONNECTIONS_TABS"
          :key="i"
          :title="tab.title"
          class="py-3 text-sm px-6 md:px-10 flex justify-center items-center transition-all duration-100 ease-in-out"
          :class="[
            isActiveConnectionsTab(i) && tab.context === 'CW'
              ? 'border border-cw-alt text-cw-alt bg-white rounded-lg'
              : isActiveConnectionsTab(i)
              ? 'border border-primary text-primary bg-white rounded-lg'
              : 'bg-truegray-100',
          ]"
          @click="() => changeConnectionsTab(i)"
        >
          <span class="sm:hidden" v-html="tab.icon" />
          <span class="hidden sm:block">
            {{ tab.name }}
          </span>
        </button>
      </div>

      <div
        class="transition-all"
        :class="animating ? 'opacity-0' : 'transition-all'"
      >
        <h2>{{ activeConnectionsTab.name }}</h2>
        <p>{{ activeConnectionsTab.title }}</p>
      </div>

      <ModalFilter
        :filters="modalFilters"
        :is-loading="isLoading"
        class="mb-10 mt-6"
        @on-submit="submitFilters"
      />

      <FilterFormActiveFilters
        v-show="showFilterFormActiveFilters"
        @on-submit="submitFilters"
      />

      <div
        v-if="activeContext !== 'CW'"
        class="flex bg-truegray-100 w-[max-content] rounded-lg overflow-hidden my-6"
      >
        <button
          v-for="(tab, i) in MY_CONNECTIONS_TABS"
          :key="i"
          :title="tab.title"
          class="py-2 text-sm px-10 flex justify-center items-center transition-all duration-100 ease-in-out"
          :class="
            isActiveMyConnectionsTab(i)
              ? [
                  MY_CONNECTIONS_TABS[i].border,
                  MY_CONNECTIONS_TABS[i].colour,
                  'bg-white rounded-lg border',
                ]
              : 'bg-truegray-100'
          "
          @click="() => changeMyConnectionsTab(i)"
        >
          {{ tab.name }}
        </button>
      </div>

      <div
        v-if="!isLoading"
        class="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-6"
      >
        <CardConnection
          v-for="connection in connections"
          :key="connection.id"
          :connection="connection"
          :context="activeContext"
        />
        <p
          v-if="!connections || (connections.length < 1 && !isLoading)"
          class="italic"
        >
          No connections found...
        </p>
      </div>
      <div v-else class="w-full h-24 flex items-center justify-center">
        <lottie :options="loadingAnimationOptions" />
      </div>

      <!-- Pagination -->
      <div
        v-if="
          connectionsPagination.previouspage || connectionsPagination.nextpage
        "
        class="flex justify-between items-center h-10 mt-8"
      >
        <span
          :disabled="!connectionsPagination.previouspage"
          :class="[
            !connectionsPagination.previouspage
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="paginateConnections(connectionsPagination.previouspage)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 17l-5-5m0 0l5-5m-5 5h12"
            />
          </svg>
        </span>
        <span class="text-center text-xs"
          >Page {{ connectionsPagination.current_page }} of
          {{ connectionsPagination.last_page }}</span
        >
        <span
          :disabled="!connectionsPagination.nextpage"
          :class="[
            !connectionsPagination.nextpage
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="paginateConnections(connectionsPagination.nextpage)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, onMounted, ref, watch } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { useRoute, useRouter } from 'vue-router/composables'
import * as loadingAnimation from '../../../shared/components/loading-dark.json'
import CardConnection from './components/CardConnection.vue'
import FilterFormActiveFilters from './components/FilterFormActiveFilters.vue'
import FormUpsertConnection from './FormUpsertConnection.vue'
import ModalFilter from './components/modal/ModalFilter.vue'
import { EventBus } from '../../../js/plugins/event-bus.js'
import { EVENT_KEYS } from '../../../js/util/constants.js'

export default defineComponent({
  name: 'TabConnections',

  components: {
    CardConnection,
    FilterFormActiveFilters,
    FormUpsertConnection,
    ModalFilter,
  },

  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()

    // LOADING/ANIMATIONS
    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isLoading = ref(false)
    const animating = ref(false)

    // DATA
    const isAdmin = computed(() => {
      return store.getters['shared/getUserAccess']
    })

    const isManagerContext = computed(() =>
      store.getters['connections/isManagerContext'](route.fullPath),
    )

    const connections = computed(
      () => store.getters[activeConnectionsTab.value.getter],
    )

    const activeContext = computed(() => {
      if (activeConnectionsTab.value?.isMyConnectionsParent) {
        return activeMyConnectionsTab.value.context
      }

      return activeConnectionsTab.value.context
    })

    const connectionsPagination = ref({
      current_page: null,
      per_page: 6,
      next_page: null,
      previous_page: null,
      last_page_url: null,
      next_page_url: null,
      prev_page_url: null,
      path: null,
      nextpage: null,
      previouspage: null,
      total: 0,
    })

    const setConnectionsPagination = (paginationObj) => {
      let prev = null
      if (paginationObj.prev_page_url) {
        prev = paginationObj.prev_page_url.split('=')
        prev = prev[1]
      }

      let next = null
      if (paginationObj.next_page_url) {
        next = paginationObj.next_page_url.split('=')
        next = next[1]
      }

      paginationObj.previouspage = prev
      paginationObj.nextpage = next

      connectionsPagination.value = paginationObj
    }

    const paginateConnections = (page) => {
      getConnections({ page })
    }

    const getConnections = async ({
      context = activeContext.value,
      page = 1, // connectionsPagination.value.current_page ?
      filters = store.getters['connections/getFilterFormPayload'],
    } = {}) => {
      isLoading.value = true
      const paginationObj = await store.dispatch(
        activeConnectionsTab.value.action,
        {
          context,
          page,
          filters,
        },
      )
      animating.value = false
      isLoading.value = false
      if (paginationObj) {
        setConnectionsPagination(paginationObj)
      }
    }

    // VIEWS/ROUTES/TABS
    const VIEW = {
      CONNECTIONS: 'connections',
      UPSERT: 'upsert',
    }
    const ROUTES_UPSERT = [
      'ManagerAddConnection',
      'ManagerUpdateConnection',
      'AddConnection',
    ]
    const CONNECTIONS_TABS = [
      {
        name: 'My Connections',
        title: 'Connections related to your local community',
        context: 'SAVED',
        action: 'connections/dispatchUpdateConnections',
        getter: 'connections/getConnections',
        isMyConnectionsParent: true,
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
                <path fill-rule="evenodd" d="M9.293 2.293a1 1 0 0 1 1.414 0l7 7A1 1 0 0 1 17 11h-1v6a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6H3a1 1 0 0 1-.707-1.707l7-7Z" clip-rule="evenodd" />
              </svg>`,
      },
      {
        name: 'CareerWise',
        title:
          'Connections that CareerWise have collected and vetted as an available resource',
        context: 'CW',
        action: 'connections/dispatchUpdateOrganisationConnections',
        getter: 'connections/getOrganisationConnections',
        icon: '<img class="h-4" src="/img/logo-cw.png" alt="CareerWise Logo" />',
      },
    ]

    const MY_CONNECTIONS_TABS = [
      {
        name: 'Saved',
        title: 'Connections that you have saved',
        context: 'SAVED',
        action: 'connections/dispatchUpdateConnections',
        getter: 'connections/getConnections',
        border: 'border-green-600',
        colour: 'text-green-600',
      },
      {
        name: 'Unsaved',
        title:
          'Connections that have signed up to support your local community',
        context: 'LOCAL',
        action: 'connections/dispatchUpdateConnections',
        getter: 'connections/getConnections',
        border: 'border-red-400',
        colour: 'text-red-400',
      },
    ]

    const activeView = ref(VIEW.CONNECTIONS)
    const activeConnectionsTab = ref(CONNECTIONS_TABS[0])
    const activeMyConnectionsTab = ref(MY_CONNECTIONS_TABS[0])
    const viewButtonIcon = computed(() => {
      return activeView.value === VIEW.CONNECTIONS
        ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
            <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
          </svg>`
        : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
            <path fill-rule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clip-rule="evenodd" />
          </svg>`
    })
    const viewButtonText = computed(() => {
      return activeView.value === VIEW.CONNECTIONS
        ? '+ Add Connection'
        : '< Back to Connections'
    })

    const isActiveConnectionsTab = (i) => {
      return activeConnectionsTab.value === CONNECTIONS_TABS[i]
    }

    const isActiveMyConnectionsTab = (i) => {
      return activeMyConnectionsTab.value === MY_CONNECTIONS_TABS[i]
    }

    const setActiveConnectionsTab = (i = 0) => {
      activeConnectionsTab.value = CONNECTIONS_TABS[i]
    }

    const setActiveMyConnectionsTab = (i = 0) => {
      activeMyConnectionsTab.value = MY_CONNECTIONS_TABS[i]
    }

    const changeConnectionsTab = async (i) => {
      animating.value = true
      // Default my connections tab to saved every time parent context is changed back to SAVED
      if (CONNECTIONS_TABS[i]?.isMyConnectionsParent) {
        setActiveMyConnectionsTab(0)
      }
      setTimeout(async () => {
        setActiveConnectionsTab(i)
        await store.dispatch('connections/dispatchInitFilters')
        await getConnections()
      }, 100)
    }

    const changeMyConnectionsTab = (i) => {
      setTimeout(() => {
        setActiveMyConnectionsTab(i)
        getConnections()
      }, 100)
    }

    const changeView = (newView = null) => {
      const toggledView =
        activeView.value === VIEW.CONNECTIONS ? VIEW.UPSERT : VIEW.CONNECTIONS
      newView = newView || toggledView
      activeView.value = newView
    }

    const routeToView = async () => {
      const name =
        activeView.value === VIEW.CONNECTIONS
          ? ROUTES_UPSERT[0]
          : 'ManagerConnections'
      if (route.name !== name) {
        router.push({ name })
      }
    }

    const watchRoute = () => {
      const value = ROUTES_UPSERT.includes(route.name)
        ? VIEW.UPSERT
        : VIEW.CONNECTIONS
      changeView(value)
      setActiveConnectionsTab()
      if (value !== VIEW.UPSERT && !route.path?.includes('reports')) {
        getConnections()
      }
    }

    watch(route, () => watchRoute())

    // FILTERS
    const modalFilters = computed(() => {
      if (activeContext.value === 'CW') {
        return ['industries', 'preferences', 'organisationsSupport']
      }

      return undefined
    })

    const selectedFiltersCount = computed(
      () => store.getters['connections/getSelectedFiltersCount'],
    )

    const showFilterFormActiveFilters = computed(
      () => selectedFiltersCount.value,
    )

    const submitFilters = async () => {
      await getConnections()
    }

    onMounted(() => {
      watchRoute()

      EventBus.$on(EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION, (paginationObj) =>
        setConnectionsPagination(paginationObj),
      )
    })

    return {
      // DATA
      isAdmin,
      loadingAnimationOptions,
      isLoading,
      animating,
      isManagerContext,

      // CONNECTIONS
      connections,
      connectionsPagination,
      paginateConnections,
      getConnections,

      // VIEWS
      VIEW,
      viewButtonIcon,
      viewButtonText,
      activeView,
      routeToView,
      changeView,

      // TABS
      activeContext,
      CONNECTIONS_TABS,
      MY_CONNECTIONS_TABS,
      activeConnectionsTab,
      activeMyConnectionsTab,
      isActiveConnectionsTab,
      isActiveMyConnectionsTab,
      changeConnectionsTab,
      changeMyConnectionsTab,

      // FILTERS
      modalFilters,
      showFilterFormActiveFilters,
      submitFilters,
    }
  },
})
</script>
